import React from 'react';
import type { TutorialStep } from '../../tutorial/TutorialManager.js';
import type { MenuControlScheme, MenuLanguage } from '../main-menu/types.js';
import './tutorial.css';

export function TutorialArrow({ direction = 'down' }: { direction?: 'down' | 'up' }) {
  return <span className={`tutorial-arrow ${direction}`} aria-hidden="true">➜</span>;
}

export function HighlightMask({ step, focusPosition }: {
  step: TutorialStep;
  focusPosition?: { x: number; y: number } | null;
}) {
  const trackedStyle = (step.highlight === 'orb'
    || step.highlight === 'bot'
    || step.highlight.startsWith('upgrade-')) && focusPosition
    ? { left: `${focusPosition.x}px`, top: `${focusPosition.y}px` }
    : undefined;
  return <div className={`tutorial-focus tutorial-focus-${step.highlight}`}
    style={trackedStyle} aria-hidden="true">
    <TutorialArrow direction={step.highlight === 'upgrades'
      || step.highlight.startsWith('upgrade-') ? 'down' : 'up'} />
  </div>;
}

export function TutorialHint({ step, language, controlScheme }: {
  step: TutorialStep;
  language: MenuLanguage;
  controlScheme: MenuControlScheme;
}) {
  const locale = language === 'fr' ? 'fr' : language === 'es' ? 'es' : 'en';
  const movementText = {
    wasd: {
      es: 'Usa WASD o las flechas para moverte.',
      en: 'Use WASD or the arrow keys to move.',
      fr: 'Utilisez WASD ou les flèches pour vous déplacer.',
    },
    mouse: {
      es: 'Mueve el ratón hacia donde quieras avanzar.',
      en: 'Move the mouse towards where you want to go.',
      fr: 'Déplacez la souris vers la direction souhaitée.',
    },
    touch: {
      es: 'Desliza el dedo para moverte.',
      en: 'Slide your finger to move.',
      fr: 'Faites glisser votre doigt pour vous déplacer.',
    },
  }[controlScheme];
  return <div className={`tutorial-hint tutorial-hint-${step.highlight}`} role="status" aria-live="polite">
    {step.id === 'move' ? movementText[locale] : step.text[locale]}
  </div>;
}

export function TutorialOverlay({ step, language, controlScheme, focusPosition, reducedMotion, onContinue, onSkip }: {
  step: TutorialStep;
  language: MenuLanguage;
  controlScheme: MenuControlScheme;
  focusPosition?: { x: number; y: number } | null;
  reducedMotion: boolean;
  onContinue: () => void;
  onSkip: () => void;
}) {
  const tr = (es: string, en: string, fr: string) =>
    language === 'es' ? es : language === 'fr' ? fr : en;
  return <div className={`tutorial-overlay ${reducedMotion ? 'reduce-motion' : ''}`}>
    <HighlightMask step={step} focusPosition={focusPosition} />
    <TutorialHint step={step} language={language} controlScheme={controlScheme} />
    {step.objective === 'learn' && (
      <button className="tutorial-continue" data-game-input="ignore" onClick={onContinue}>
        {tr('Continuar', 'Continue', 'Continuer')}
      </button>
    )}
    <button className="tutorial-skip" data-game-input="ignore" onClick={onSkip}>
      {tr('Saltar tutorial', 'Skip tutorial', 'Passer le tutoriel')}
    </button>
  </div>;
}
